/**
 * Thinking control for the chat composer.
 *
 * Labelled "Thinking" rather than "Effort" on purpose. Effort is the right word
 * for a ladder, and it is what the providers call the wire field — but this app
 * also serves models that only do on/off, and "Effort: Off" misdescribes those:
 * the model still answers, it just does not think first. "Thinking" is the one
 * noun that stays true for both shapes, so the user-facing label uses it and
 * "effort" stays internal (`reasoningEffort`, `reasoning_effort`).
 *
 * Renders the rungs the SELECTED MODEL actually supports and nothing else. The
 * list is computed server-side (the model's declared ladder intersected with
 * what our SDK version can transmit) and arrives on the model row as
 * `reasoningOptions`, so this component never needs to know a provider's wire
 * format — or that providers disagree about one.
 *
 * It renders nothing at all when there is no real choice: a model that declared
 * no reasoning capability, or one whose provider offers a single fixed setting.
 * A disabled dial reads as a bug; an absent one reads as "this model doesn't do
 * that".
 */

import { Brain, Check, ChevronDown } from "lucide-react";
import type { ReasoningCapability, ReasoningEffort } from "@taqat/core/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * How a model expresses reasoning control.
 *
 * Taken straight off the capability union so the two cannot drift: add a mode
 * to `reasoningCapabilitySchema` and every consumer of this type stops
 * compiling until it decides how to draw it.
 */
export type ReasoningMode = ReasoningCapability["mode"];

/** Human labels. The wire values are lowercase; these are what people read. */
const EFFORT_LABEL: Record<ReasoningEffort, string> = {
  none: "Off",
  minimal: "Minimal",
  low: "Low",
  medium: "Medium",
  high: "High",
  xhigh: "Very high",
  max: "Maximum",
};

/**
 * One line of plain-English consequence per rung.
 *
 * Reasoning tokens bill as output tokens, so a higher rung costs real money and
 * real latency. Saying so at the point of choice is cheaper than explaining it
 * on an invoice later.
 */
const EFFORT_HINT: Record<ReasoningEffort, string> = {
  none: "No thinking — fastest, cheapest",
  minimal: "Barely any thinking",
  low: "Quick thinking — good for simple asks",
  medium: "Balanced thinking",
  high: "Thorough — slower, costs more",
  xhigh: "Very thorough — noticeably slower",
  max: "Maximum thinking — slowest, most expensive",
};

interface ReasoningEffortSelectorProps {
  /** Rungs this model supports, ascending. Fewer than two renders nothing. */
  options?: ReasoningEffort[];
  /**
   * How this model expresses reasoning. A `toggle` model has no gradations —
   * every level it accepts produces the same amount of thinking — so its
   * choices are named On/Off rather than borrowed from the effort ladder.
   *
   * Derived from the capability union rather than spelled out, so adding a mode
   * to the schema breaks here instead of silently landing in the ladder branch.
   */
  mode?: ReasoningMode;
  /** Current selection. Undefined means "use the model's own default". */
  value?: ReasoningEffort;
  onChange: (effort: ReasoningEffort | undefined) => void;
  disabled?: boolean;
  /**
   * The model this chat will use isn't resolved yet.
   *
   * Distinct from "this model has no rungs": both would otherwise render
   * nothing, so the control popped into the toolbar once the model list
   * arrived — and, when the model was changed from settings, briefly showed
   * the PREVIOUS model's rungs before correcting itself. Holding the space
   * with a skeleton keeps the toolbar from reflowing and stops the stale
   * control being clickable while it is still wrong.
   */
  loading?: boolean;
}

export function ReasoningEffortSelector({
  options,
  mode,
  value,
  onChange,
  disabled,
  loading,
}: ReasoningEffortSelectorProps) {
  /*
    Sized to the capsule it stands in for, so the send button and mic don't
    shift when the real control replaces it.
  */
  if (loading) {
    return (
      <Skeleton
        aria-hidden
        className="h-11 w-[88px] shrink-0 rounded-full lg:h-8"
      />
    );
  }

  /*
    One option is not a choice — a model whose provider only accepts a single
    fixed setting has nothing to offer, so the control stays out of the toolbar
    entirely rather than sitting there inert.
  */
  if (!options || options.length < 2) return null;

  /*
    A toggle model is presented as On/Off, not as a slice of the effort ladder.

    Measured on a LiteLLM proxy serving DeepSeek: omitting the parameter and
    sending `none` both produce zero reasoning, while every level from `minimal`
    to `max` produces the same ~2,900 characters. Rendering that as
    "Default / Off / High" is wrong twice over — two of the three options do the
    same thing, and "High" implies a Low and Medium that do not exist. The
    honest control is a switch, so that is what gets drawn.
  */
  const isToggle = mode === "toggle";
  /** The rung that means "think" on a toggle model. */
  const onRung = options.find((option) => option !== "none");

  if (isToggle && onRung) {
    const isOn = value === onRung;
    return (
      <ToggleControl
        isOn={isOn}
        disabled={disabled}
        onSelect={(next) => onChange(next ? onRung : "none")}
      />
    );
  }

  const activeLabel = value ? EFFORT_LABEL[value] : "Default";
  /*
    "Default" is the model's own behaviour, so it stays neutral; any explicit
    rung is the user having taken control, and reads in the accent — the same
    signal the toggle uses for On, so the two controls answer "is thinking
    configured?" the same way from across the composer.
  */
  const isCustomised = Boolean(value);

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <button
              type="button"
              className={cn(
                // Same hairline capsule as the toggle variant: both are the
                // same control wearing different clothes, so they must not
                // read as two unrelated widgets sitting in one toolbar.
                "inline-flex items-center gap-1.5 rounded-full border pl-2 pr-1.5",
                "h-11 transition-colors lg:h-8",
                "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "disabled:cursor-not-allowed disabled:opacity-50",
                isCustomised ? "border-primary/30" : "border-border/60",
              )}
              aria-label={`Thinking: ${activeLabel}`}
            >
              <Brain
                className={cn(
                  "size-3.5 shrink-0 transition-colors",
                  isCustomised ? "text-primary" : "text-muted-foreground",
                )}
              />
              <span
                className={cn(
                  "text-[11px] font-medium transition-colors",
                  isCustomised ? "text-primary" : "text-foreground",
                )}
              >
                {activeLabel}
              </span>
              <ChevronDown
                className={cn(
                  "size-3 shrink-0 transition-colors",
                  isCustomised ? "text-primary/70" : "text-muted-foreground",
                )}
              />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>How much the model thinks before answering</p>
        </TooltipContent>
      </Tooltip>

      {/* Right-aligned: the trigger now sits in the composer's right-hand
          cluster, so anchoring the menu to its start edge would push it off
          the screen on narrow viewports. */}
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Thinking
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/*
          "Default" is a real option, not an absence: sending nothing means the
          provider's own default, which is medium on OpenAI, high on Anthropic,
          and max on some others. Letting the user return to it matters.
        */}
        <EffortItem
          label="Default"
          hint="Whatever this model does on its own"
          selected={!value}
          onSelect={() => onChange(undefined)}
        />

        {options.map((effort) => (
          <EffortItem
            key={effort}
            label={EFFORT_LABEL[effort]}
            hint={EFFORT_HINT[effort]}
            selected={value === effort}
            onSelect={() => onChange(effort)}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * One rung in the menu.
 *
 * The selected row is marked with a check as well as a tint: the tint alone is
 * a few percent of background and disappears against some themes, which left
 * the menu with no answer to "which one am I on right now".
 */
function EffortItem({
  label,
  hint,
  selected,
  onSelect,
}: {
  label: string;
  hint: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      onSelect={onSelect}
      className={cn("gap-2", selected && "bg-accent")}
    >
      <Check
        className={cn(
          "size-3.5 shrink-0 self-start text-primary",
          // Held in the layout rather than removed, so the labels of the
          // unselected rows stay on the same left edge as the selected one.
          !selected && "invisible",
        )}
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className={cn("text-sm", selected && "font-medium text-primary")}>
          {label}
        </span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </span>
    </DropdownMenuItem>
  );
}

/**
 * On/off control for a model with no real gradations.
 *
 * Two segments rather than a menu or a press-to-toggle pill: a menu costs two
 * clicks to do what one can, and a lit pill is ambiguous at a glance — it reads
 * equally as "thinking is on" and "click to turn thinking on". Showing both
 * choices removes the guess.
 *
 * Four details do the work of making it feel built rather than assembled:
 *
 *  - The icon lives INSIDE the capsule. Sitting outside with a gap, it read as
 *    a separate button that happened to do nothing.
 *  - The segments are equal width, so the raised pill keeps its size when the
 *    choice changes. Letting "Off" and "On" size themselves made the control
 *    twitch on every click.
 *  - The capsule is a hairline, not a filled slab. Every other control in this
 *    toolbar is a ghost icon and the send button is the only solid shape;
 *    a secondary per-turn setting must not outweigh the primary action.
 *  - The on state is carried by colour as well as position, so "is thinking on"
 *    is answerable from across the composer without reading the word.
 */
function ToggleControl({
  isOn,
  disabled,
  onSelect,
}: {
  isOn: boolean;
  disabled?: boolean;
  onSelect: (on: boolean) => void;
}) {
  const segment = (on: boolean, label: string) => {
    const selected = isOn === on;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        aria-label={`Thinking ${label.toLowerCase()}`}
        disabled={disabled}
        onClick={() => onSelect(on)}
        className={cn(
          // Fixed width keeps the raised pill from resizing between states.
          "h-full w-9 rounded-full text-[11px] font-medium transition-all lg:w-8",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed",
          selected
            ? cn(
                "bg-background shadow-sm",
                on ? "text-primary" : "text-foreground",
              )
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        {label}
      </button>
    );
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          role="radiogroup"
          aria-label="Thinking"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border pl-2 pr-1",
            "h-11 transition-colors lg:h-8",
            isOn ? "border-primary/30" : "border-border/60",
            disabled && "opacity-50",
          )}
        >
          <Brain
            className={cn(
              "size-3.5 shrink-0 transition-colors",
              isOn ? "text-primary" : "text-muted-foreground",
            )}
          />
          {/* Inset track: the selected segment reads as raised out of it. */}
          <div className="inline-flex h-9 items-center rounded-full bg-muted/60 p-0.5 lg:h-6">
            {segment(false, "Off")}
            {segment(true, "On")}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>Whether the model thinks before answering</p>
      </TooltipContent>
    </Tooltip>
  );
}
